import { defineNuxtPlugin } from '#app';
import { EventBus } from '~/composables/bus';

interface TooltipBinding {
  value: string
  arg?: 'top' | 'bottom'
}

interface TooltipHandlers {
  mouseEnterHandler: (e: MouseEvent) => void
  mouseLeaveHandler: () => void
  touchStartHandler: (e: TouchEvent) => void
  touchEndHandler: () => void
  touchCancelHandler: () => void
}

// Расширяем HTMLElement для типизации
declare global {
  interface HTMLElement {
    _tooltipHandlers?: TooltipHandlers
    _tooltipTimer?: NodeJS.Timeout
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const showTooltip = (target: HTMLElement, binding: TooltipBinding, clientX?: number) => {
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const position = binding.arg || 'top';

    // Если есть координаты тача, используем их, иначе центр элемента
    const x = clientX !== undefined
      ? clientX + window.pageXOffset
      : rect.left + rect.width / 2 + window.pageXOffset;

    EventBus.$emit('show_tooltip', {
      text: binding.value,
      x,
      y: position === 'top' ? rect.top + window.pageYOffset : rect.bottom + window.pageYOffset,
      position,
    });
  };

  const hideTooltip = (el: HTMLElement) => {
    if (el._tooltipTimer) {
      clearTimeout(el._tooltipTimer);
      el._tooltipTimer = undefined;
    }
    EventBus.$emit('hide_tooltip');
  };

  nuxtApp.vueApp.directive('tooltip', {
    mounted(el: HTMLElement, binding: { value: string, arg?: 'top' | 'bottom' }) {
      const tooltipBinding: TooltipBinding = {
        value: binding.value,
        arg: binding.arg,
      };

      // Создаем обработчики событий
      const mouseEnterHandler = (e: MouseEvent) => {
        showTooltip(e.target as HTMLElement, tooltipBinding);
      };

      const mouseLeaveHandler = () => {
        hideTooltip(el);
      };

      const touchStartHandler = (e: TouchEvent) => {
        const touch = e.touches[0];
        el._tooltipTimer = setTimeout(() => {
          showTooltip(e.target as HTMLElement, tooltipBinding, touch.clientX, touch.clientY);
        }, 500);
      };

      const touchEndHandler = () => {
        hideTooltip(el);
      };

      const touchCancelHandler = () => {
        hideTooltip(el);
      };

      // Сохраняем ссылки на обработчики
      el._tooltipHandlers = {
        mouseEnterHandler,
        mouseLeaveHandler,
        touchStartHandler,
        touchEndHandler,
        touchCancelHandler,
      };

      // Добавляем обработчики событий
      el.addEventListener('mouseenter', mouseEnterHandler);
      el.addEventListener('mouseleave', mouseLeaveHandler);
      el.addEventListener('touchstart', touchStartHandler, { passive: true });
      el.addEventListener('touchend', touchEndHandler, { passive: true });
      el.addEventListener('touchcancel', touchCancelHandler, { passive: true });
    },

    updated(el: HTMLElement, binding: { value: string, arg?: 'top' | 'bottom' }) {
      // Обновляем текст тултипа если он изменился
      EventBus.$emit('update_tooltip_text', {
        text: binding.value,
      });
    },

    unmounted(el: HTMLElement) {
      // Очищаем таймер если он есть
      if (el._tooltipTimer) {
        clearTimeout(el._tooltipTimer);
        el._tooltipTimer = undefined;
      }

      // Удаляем обработчики событий
      const handlers = el._tooltipHandlers;
      if (handlers) {
        el.removeEventListener('mouseenter', handlers.mouseEnterHandler);
        el.removeEventListener('mouseleave', handlers.mouseLeaveHandler);
        el.removeEventListener('touchstart', handlers.touchStartHandler);
        el.removeEventListener('touchend', handlers.touchEndHandler);
        el.removeEventListener('touchcancel', handlers.touchCancelHandler);

        // Очищаем ссылки
        delete el._tooltipHandlers;
      }
    },
  });
});
